import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getAircraft,
  getHoursCounters,
  getTachInfo,
  type TachInfo,
} from '../../api';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useAuth } from '../../hooks/useAuth';
import { useFeedback } from '../../hooks/useFeedback';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatMetric } from '../../utils/formatMetric';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import AvgFuelCard from '../../components/AvgFuelCard/AvgFuelCard';
import FuelOilModal from '../../components/FuelOilModal/FuelOilModal';
import TachometerCard from '../../components/TachometerCard/TachometerCard';
import '../AircraftPage/AircraftPage.css';
import './TechnicalConditionPage.css';

type MetricCardProps = {
  label: string;
  value: string;
  unit?: string;
};

const MetricCard = ({ label, value, unit }: MetricCardProps) => (
  <div className="aircraft-hub-card is-static">
    <span className="aircraft-hub-label">{label}</span>
    <span className="aircraft-hub-value">
      <span className="aircraft-hub-number">
        {value}
        {value !== '—' && unit ? (
          <span className="aircraft-hub-unit">{unit}</span>
        ) : null}
      </span>
    </span>
  </div>
);

const TechnicalConditionPage = () => {
  const { aircraftId, dataRevision } = useAircraft();
  const { canMaintain, isAdmin } = useAuth();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const [airframeHours, setAirframeHours] = useState<number | null>(null);
  const [engineHours, setEngineHours] = useState<number | null>(null);
  const [tachInfo, setTachInfo] = useState<TachInfo | null>(null);
  const [avgFuelBurn, setAvgFuelBurn] = useState<number | null>(null);
  const [fuelPrice, setFuelPrice] = useState<number | null>(null);
  const [fuelOilOpen, setFuelOilOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [hoursRes, tachRes, aircraftRes] = await Promise.all([
        getHoursCounters(),
        getTachInfo(),
        getAircraft(),
      ]);
      setAirframeHours(hoursRes.data.airframe_hours ?? null);
      setEngineHours(hoursRes.data.engine_hours ?? null);
      setTachInfo(tachRes.data);
      setAvgFuelBurn(aircraftRes.data.avg_fuel_burn_lph ?? null);
      setFuelPrice(aircraftRes.data.fuel_price_per_liter ?? null);
    } catch (error) {
      setAirframeHours(null);
      setEngineHours(null);
      setTachInfo(null);
      setAvgFuelBurn(null);
      setFuelPrice(null);
      showError(
        getApiErrorMessage(error, 'Ошибка загрузки технического состояния')
      );
    }
  }, [showError]);

  useEffect(() => {
    void loadData();
  }, [aircraftId, dataRevision, loadData]);

  return (
    <div className="aircraft-page">
      {feedbackNode}
      <div className="aircraft-hub">
        <div className="aircraft-header">
          <PageBackLink to="/aircraft">← Самолёт</PageBackLink>
          <h2>Техническое состояние</h2>
        </div>

        <div className="aircraft-hub-grid condition-metrics">
          <MetricCard
            label="Налёт планера"
            value={formatMetric(airframeHours)}
            unit="м/ч"
          />
          <MetricCard
            label="Налёт двигателя"
            value={formatMetric(engineHours)}
            unit="м/ч"
          />
          <TachometerCard
            tachInfo={tachInfo}
            canMaintain={canMaintain}
            onReplaced={(info) => {
              setTachInfo(info);
              showSuccess(
                `Тахометр заменён. Налёт не меняется (${info.motohours_tip} м/ч), новый отсчёт прибора с ${info.tach_current}`
              );
            }}
          />
          <AvgFuelCard
            avgFuelBurn={avgFuelBurn}
            fuelPrice={fuelPrice}
            canEdit={isAdmin}
            onSaved={(next) => {
              setAvgFuelBurn(next.avg_fuel_burn_lph);
              setFuelPrice(next.fuel_price_per_liter);
              showSuccess('Расход сохранён');
            }}
          />
        </div>

        <div className="condition-nav">
          <Link to="/maintenance" className="condition-nav-item">
            Периодическое ТО
          </Link>
          <Link to="/issues" className="condition-nav-item">
            Неисправности
          </Link>
          <Link to="/modernization" className="condition-nav-item">
            Модернизация
          </Link>
          <button
            type="button"
            className="condition-nav-item"
            onClick={() => setFuelOilOpen(true)}
          >
            Расход топлива и масла
          </button>
        </div>
      </div>
      {fuelOilOpen ? (
        <FuelOilModal onClose={() => setFuelOilOpen(false)} />
      ) : null}
    </div>
  );
};

export default TechnicalConditionPage;
